import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import '../dashboard/dashboard.css';

interface AdminUser {
  id: string;
  email: string;
  fullName: string;
  role: 'customer' | 'operator';
  status: 'active' | 'suspended';
  emailVerified: boolean;
  createdAt: string;
}

export function AdminUsersPage() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resetLink, setResetLink] = useState<{ email: string; url: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') ?? '';
  const status = searchParams.get('status') ?? '';

  function loadUsers() {
    setLoading(true);
    setError(null);
    const query = new URLSearchParams();
    if (search) query.set('search', search);
    if (status) query.set('status', status);
    api
      .get<{ users: AdminUser[] }>(`/admin/users${query.toString() ? `?${query.toString()}` : ''}`)
      .then((data: { users: AdminUser[] }) => setUsers(data.users))
      .catch((err: Error) => setError(err.message || 'Could not load users.'))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status]);

  function updateSearch(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('search', value);
    else next.delete('search');
    setSearchParams(next, { replace: true });
  }

  function updateStatus(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('status', value);
    else next.delete('status');
    setSearchParams(next, { replace: true });
  }

  async function makeResetLink(user: AdminUser) {
    setError(null);
    setCopied(false);
    try {
      const result = await api.post<{ email: string; url: string }>(`/admin/users/${user.id}/reset-link`, {});
      setResetLink({ email: result.email, url: result.url });
    } catch (err) {
      setError((err as Error).message || 'Could not create a reset link.');
    }
  }

  async function copyResetLink() {
    if (!resetLink) return;
    try {
      await navigator.clipboard.writeText(resetLink.url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  async function toggleSuspend(userId: string, suspend: boolean) {
    setError(null);
    try {
      await api.post(`/admin/users/${userId}/${suspend ? 'suspend' : 'unsuspend'}`, {});
      loadUsers();
    } catch (err) {
      setError((err as Error).message || 'Could not update user.');
    }
  }

  return (
    <div className="admin-users">
      <h1>Users</h1>
      <div className="filters">
        <input
          type="search"
          placeholder="Search by email or name"
          value={search}
          onChange={(e) => updateSearch(e.target.value)}
        />
        <select value={status} onChange={(e) => updateStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      {resetLink ? (
        <div className="sarnia-card admin-reset-link" role="status">
          <strong>Password reset link for {resetLink.email}</strong>
          <p className="dash-page-subtitle">Send this to them yourself. It works once and expires in one hour.</p>
          <input type="text" readOnly value={resetLink.url} aria-label="Reset link" onFocus={(e) => e.currentTarget.select()} style={{ width: '100%' }} />
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button type="button" className="sarnia-btn sarnia-btn--primary sarnia-btn--sm" onClick={copyResetLink}>
              {copied ? 'Copied' : 'Copy link'}
            </button>
            <button type="button" className="sarnia-btn sarnia-btn--secondary sarnia-btn--sm" onClick={() => setResetLink(null)}>
              Close
            </button>
          </div>
        </div>
      ) : null}

      {loading && <p className="dash-page-subtitle">Loading users…</p>}
      {error && (
        <div className="sarnia-card" style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}>
          {error}
        </div>
      )}

      {!loading && !error && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th>Status</th>
              <th>Verified</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>{user.email}</td>
                <td>{user.fullName}</td>
                <td>{user.role}</td>
                <td>
                  <span
                    className={`sarnia-badge ${
                      user.status === 'active'
                        ? 'sarnia-badge--success'
                        : 'sarnia-badge--danger'
                    }`}
                  >
                    {user.status}
                  </span>
                </td>
                <td>{user.emailVerified ? 'Yes' : 'No'}</td>
                <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button type="button" className="sarnia-btn sarnia-btn--secondary sarnia-btn--sm" onClick={() => makeResetLink(user)} style={{ marginRight: '0.4rem' }}>
                    Reset link
                  </button>
                  {user.status === 'active' ? (
                    <button type="button" className="sarnia-btn sarnia-btn--secondary sarnia-btn--sm" onClick={() => toggleSuspend(user.id, true)}>
                      Suspend
                    </button>
                  ) : (
                    <button type="button" className="sarnia-btn sarnia-btn--secondary sarnia-btn--sm" onClick={() => toggleSuspend(user.id, false)}>
                      Unsuspend
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!loading && !error && users.length === 0 && <p className="dash-page-subtitle">No users match.</p>}
    </div>
  );
}
